import { CSSProperties, ForwardedRef, forwardRef } from 'react';
import { Control, FieldValues, FieldPath, UseControllerProps, useController } from 'react-hook-form';
import { isNotEmpty } from '~/utils/helpers';
import BankAccountSelect, { BankAccountSelectProps } from '../inputs/Select/BankAccountSelect';

interface Props<T extends FieldValues = FieldValues, TName extends FieldPath<T> = FieldPath<T>> {
  className?: string;
  control: Control<T>;
  disabled?: boolean;
  filterAccount?: BankAccountSelectProps['filterAccount'];
  helpText?: BankAccountSelectProps['helpText'];
  hideLabel?: BankAccountSelectProps['hideLabel'];
  label: string;
  name: TName;
  required?: boolean;
  emptyOption?: BankAccountSelectProps['emptyOption'];
  placeholder?: string;
  hideNoAccount?: BankAccountSelectProps['hideNoAccount'];
  rules?: UseControllerProps<T, TName>['rules'];
  style?: CSSProperties;
  disableAccountsWithNoPaymentsFlag?: BankAccountSelectProps['disableAccountsWithNoPaymentsFlag'];
  disableAccountPredicate?: BankAccountSelectProps['disableAccountPredicate'];
}

interface WithForwardRefTypeProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> extends React.FC<Props<T, TName>> {
  <T extends FieldValues = FieldValues, TName extends FieldPath<T> = FieldPath<T>>(
    props: Props<T, TName>,
  ): ReturnType<React.FC<Props<T, TName>>>;
}

const BankAccountController: WithForwardRefTypeProps = forwardRef(
  (
    {
      className,
      control,
      filterAccount,
      helpText,
      name,
      disabled,
      emptyOption: emptyOption,
      placeholder,
      label,
      required,
      rules,
      style,
      hideLabel,
      hideNoAccount,
      disableAccountsWithNoPaymentsFlag,
      disableAccountPredicate,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { field, fieldState } = useController({ name, control, rules });
    return (
      <BankAccountSelect
        className={className}
        emptyOption={emptyOption}
        placeholder={placeholder}
        onChange={(val) => field.onChange(val)}
        label={label}
        helpText={helpText}
        isDisabled={disabled}
        value={isNotEmpty(field.value) ? field.value : null}
        isRequired={required}
        errorMessage={fieldState.error?.message}
        style={style}
        filterAccount={filterAccount}
        hideLabel={hideLabel}
        hideNoAccount={hideNoAccount}
        ref={ref}
        disableAccountsWithNoPaymentsFlag={disableAccountsWithNoPaymentsFlag}
        disableAccountPredicate={disableAccountPredicate}
      />
    );
  },
);

export default BankAccountController;
